import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { invalidateForTask } from "@/lib/useTaskMutations";
import { Icon } from "./Icon";
import { Pill } from "./Pill";
import { TASK_STATUS_LABEL } from "@/lib/taskStatus";
import type { TaskDependencyRef } from "@shared/types";

/**
 * Blocked-by / Blocking lists on the task detail. Adding a blocker posts to
 * /blockers (server rejects self + cycles). Removing works from either side.
 */
export function DependencySection({
  taskId,
  projectId,
  onChanged,
}: {
  taskId: string;
  projectId: string;
  // Fired after a blocker is added/removed. The Gantt uses it to refetch its
  // project-wide edge set so dependency arrows redraw live; TaskDetail omits it.
  onChanged?: () => void;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deps = useQuery({
    queryKey: queryKeys.taskDeps(taskId),
    queryFn: () => api.taskDependencies(taskId),
  });
  const projectTasks = useQuery({
    queryKey: queryKeys.tasksFor(projectId),
    queryFn: () => api.listTasks(projectId),
  });

  function refresh(otherId?: string) {
    qc.invalidateQueries({ queryKey: queryKeys.taskDeps(taskId) });
    invalidateForTask(qc, { projectId, taskId });
    if (otherId) {
      qc.invalidateQueries({ queryKey: queryKeys.task(otherId) });
      qc.invalidateQueries({ queryKey: queryKeys.taskDeps(otherId) });
    }
    onChanged?.();
  }

  const add = useMutation({
    mutationFn: (blockerId: string) => api.addTaskBlocker(taskId, blockerId),
    onSuccess: (_d, blockerId) => {
      setAdding(false);
      setError(null);
      refresh(blockerId);
    },
    onError: (e: Error) => setError(e.message || "Couldn't add that blocker."),
  });

  const removeBlocker = useMutation({
    mutationFn: (blockerId: string) => api.removeTaskBlocker(taskId, blockerId),
    onSuccess: (_d, blockerId) => refresh(blockerId),
  });
  // Removing a "blocking" edge: this task blocks `blockedId`, so the edge is
  // (taskId blocks blockedId) — delete it from blockedId's perspective.
  const removeBlocking = useMutation({
    mutationFn: (blockedId: string) => api.removeTaskBlocker(blockedId, taskId),
    onSuccess: (_d, blockedId) => refresh(blockedId),
  });

  const blockedBy = deps.data?.blockedBy ?? [];
  const blocks = deps.data?.blocks ?? [];

  // Candidate blockers: other tasks in the project not already a blocker.
  const existingBlockerIds = new Set(blockedBy.map((b) => b.id));
  const candidates = (projectTasks.data?.tasks ?? []).filter(
    (t) => t.id !== taskId && !existingBlockerIds.has(t.id),
  );

  return (
    <section className="dep-section">
      <h3 className="dep-h">
        <Icon name="flag" size={14} /> Dependencies
      </h3>

      <div className="dep-group">
        <div className="dep-label">Blocked by</div>
        {blockedBy.length === 0 ? (
          <span className="t-muted t-sm">Nothing — ready to start.</span>
        ) : (
          <ul className="dep-list">
            {blockedBy.map((d) => (
              <DepRow
                key={d.id}
                dep={d}
                onRemove={() => removeBlocker.mutate(d.id)}
              />
            ))}
          </ul>
        )}
        {adding ? (
          <div className="dep-add">
            <select
              className="select"
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) add.mutate(e.target.value);
              }}
              aria-label="Choose a blocking task"
            >
              <option value="">Select a task…</option>
              {candidates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="chip-btn"
              onClick={() => {
                setAdding(false);
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="chip-btn dep-add-btn"
            onClick={() => setAdding(true)}
          >
            <Icon name="plus" size={12} /> Add blocker
          </button>
        )}
        {error && <div className="t-danger t-sm">{error}</div>}
      </div>

      <div className="dep-group">
        <div className="dep-label">Blocking</div>
        {blocks.length === 0 ? (
          <span className="t-muted t-sm">Not blocking anything.</span>
        ) : (
          <ul className="dep-list">
            {blocks.map((d) => (
              <DepRow
                key={d.id}
                dep={d}
                onRemove={() => removeBlocking.mutate(d.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function DepRow({
  dep,
  onRemove,
}: {
  dep: TaskDependencyRef;
  onRemove: () => void;
}) {
  return (
    <li className="dep-row">
      <Pill variant={dep.status} dot>
        {TASK_STATUS_LABEL[dep.status]}
      </Pill>
      <Link
        to="/tasks/$taskId"
        params={{ taskId: dep.id }}
        className="dep-link"
      >
        {dep.title}
      </Link>
      <button
        type="button"
        className="icon-btn dep-rm"
        aria-label={`Remove dependency ${dep.title}`}
        onClick={onRemove}
      >
        ✕
      </button>
    </li>
  );
}
